import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { Modal } from "@mui/material";
import { useParams } from "react-router-dom";
import { useRecoilValue } from "recoil";
import CloseIcon from "@mui/icons-material/Close";
import {
  UpfrontPaymentButtonRow,
  UpfrontPaymentFinancialModalWrapper,
  UpfrontPaymentOptional,
  UpfrontPaymentSaveCreditCardGrid,
  UpfrontPaymentStripeInput,
  UpfrontPaymentStripeInputError,
  UpfrontPaymentStripeInputRow,
  UpfrontPaymentStripePaymentDisclosure,
} from "./UpfrontPayment.styles";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import { useBillPreviewsRefresh } from "../../utils/hooks/useBillPreviewsRefresh";
import { Button } from "../shared/Button/Button";
import { StyledButton } from "../shared/Button/Button.styles";
import { UpfrontPaymentReceipt } from "./UpfrontPaymentReceipt";
import {
  PayWithSavedCardUpfront,
  PostUpfrontPaymentPlanSuccess,
  PostUpfrontPaymentSuccess,
  Bill,
  getCentsToDollars,
} from "./Services/UpfrontPaymentService";
import { BillingSupportNumber, StepperIndex } from "./types";
import { getUserAuth } from "../../recoil/selectors";
import { validateEmail } from "../../utils/hooks/helpers";
import { SpinnerLoading } from "../shared/Spinner/SpinnerLoading";
import { ErrorPopup } from "../shared/ErrorPopup/ErrorPopup";
import {
  ProfileModalTitle,
  ProfileModalTitleWrapper,
} from "../shared/Modal/Modal.styles";

export interface UpfrontPaymentStripeContentProps {
  stripeModalOpen: boolean;
  setStripeModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  paymentSubmitted: boolean;
  setPaymentSubmitted: React.Dispatch<React.SetStateAction<boolean>>;
  setUpfrontBill: React.Dispatch<React.SetStateAction<Bill | undefined>>;
  setActiveStep: React.Dispatch<React.SetStateAction<StepperIndex>>;
  currentStep: StepperIndex;
  clientSecret: string;
  paymentAmount: number;
  upfrontPayment: Bill;
  useSavedPayment?: string;
  useNewCard: boolean;
}

export const UpfrontPaymentStripeContent: React.FC<
  UpfrontPaymentStripeContentProps
> = ({
  stripeModalOpen,
  setStripeModalOpen,
  clientSecret,
  paymentAmount,
  setPaymentSubmitted,
  paymentSubmitted,
  currentStep,
  setUpfrontBill,
  setActiveStep,
  useSavedPayment,
  upfrontPayment,
  useNewCard,
}) => {
  const { deviceType } = useDeviceHook();
  const { t } = useTranslation();
  const auth = useRecoilValue(getUserAuth);
  const refreshBillPreviews = useBillPreviewsRefresh();
  const { billingPK, adminID } = useParams<{
    billingPK: string;
    adminID?: string;
  }>();

  const stripe = useStripe();
  const elements = useElements();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [stripeReady, setStripeReady] = useState(false);
  const [saveCard, setSaveCard] = useState(false);

  useEffect(() => {
    // Prompt confirmation when reload page is triggered
    window.onbeforeunload = () => "";

    // Unmount the window.onbeforeunload event
    return () => {
      window.onbeforeunload = null;
    };
  }, []);

  useEffect(() => {
    if (currentStep !== StepperIndex.PaymentPlan && !clientSecret) {
      setErrorMessage(
        t("upfrontPayment.errors.contactForDetails", {
          phone: BillingSupportNumber,
        }),
      );
    }
  }, [clientSecret]);

  const handleSubmit: React.FormEventHandler = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) {
      return;
    }

    if (loading) {
      return;
    }

    if (useSavedPayment && auth) {
      setLoading(true);
      const savedPaymentResponse = await PayWithSavedCardUpfront(
        billingPK,
        useSavedPayment,
        auth?.email || email,
        currentStep === StepperIndex.PaymentPlan
          ? "payment_plan"
          : "pay_in_full",
        auth?.access_token,
      );
      const responseJson = await savedPaymentResponse.json();
      setUpfrontBill(responseJson);
      setPaymentSubmitted(true);
      setLoading(false);

      return;
    }

    await elements.submit();
    setErrorMessage("");
    setLoading(true);

    if (currentStep === StepperIndex.PaymentPlan) {
      const paymentPlanStripeResponse = await stripe.createPaymentMethod({
        elements,
        params: {
          metadata: {
            bill_pk: billingPK,
          },
        },
      });

      if (paymentPlanStripeResponse?.paymentMethod?.id) {
        const paymentPlanSyncResponse = await PostUpfrontPaymentPlanSuccess(
          billingPK,
          paymentPlanStripeResponse.paymentMethod.id,
          auth ? auth.email : email,
          saveCard,
          adminID,
        );
        setLoading(false);
        if (paymentPlanSyncResponse?.ok) {
          const paymentPlanSyncJson = await paymentPlanSyncResponse.json();
          setPaymentSubmitted(true);
          setUpfrontBill(paymentPlanSyncJson.bill);
          await refreshBillPreviews();
        } else if (paymentPlanSyncResponse.status === 400) {
          const paymentPlanSyncJson = await paymentPlanSyncResponse.json();
          setErrorMessage(paymentPlanSyncJson.error);
        }
      }
      setLoading(false);
    } else if (clientSecret) {
      const oneTimePaymentMethodResponse = await stripe.createPaymentMethod({
        elements,
        params: {
          metadata: {
            bill_pk: billingPK,
          },
        },
      });

      const oneTimePaymentStripeResponse = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: oneTimePaymentMethodResponse.paymentMethod?.id || "",
          ...(auth && { setup_future_usage: "off_session" }),
        },
      );

      if (oneTimePaymentStripeResponse.error) {
        setLoading(false);
        setErrorMessage(oneTimePaymentStripeResponse.error.message);
      } else if (
        oneTimePaymentStripeResponse.paymentIntent.status === "succeeded"
      ) {
        const oneTimePaymentSyncResponse = await PostUpfrontPaymentSuccess(
          billingPK,
          clientSecret,
          auth ? auth.email : email,
          saveCard,
          adminID,
          auth ? oneTimePaymentMethodResponse.paymentMethod?.id : undefined,
        );
        setLoading(false);
        if (oneTimePaymentSyncResponse?.ok) {
          const oneTimePaymentSyncJson =
            await oneTimePaymentSyncResponse.json();
          setUpfrontBill(oneTimePaymentSyncJson);
          setPaymentSubmitted(true);
          await refreshBillPreviews();
          setLoading(false);
        }
      }
    }
  };

  useEffect(() => {
    const sendPrePayment = async () => {
      if (
        useSavedPayment &&
        auth &&
        upfrontPayment.portal_bill_status === "outstanding" &&
        stripeModalOpen
      ) {
        setLoading(true);
        const savedPaymentResponse = await PayWithSavedCardUpfront(
          billingPK,
          useSavedPayment,
          auth?.email || email,
          currentStep === StepperIndex.PaymentPlan
            ? "payment_plan"
            : "pay_in_full",
          auth?.access_token,
        );
        if (savedPaymentResponse?.ok) {
          const responseJson = await savedPaymentResponse.json();
          setUpfrontBill(responseJson);
          setPaymentSubmitted(true);
          setLoading(false);
        }
      }
    };
    sendPrePayment();
  }, [stripeModalOpen]);

  return (
    <Modal
      open={stripeModalOpen}
      onClose={() => setStripeModalOpen(false)}
      style={{
        alignItems: "center",
        justifyContent: "center",
        display: "flex",
        pointerEvents: loading ? "none" : undefined,
      }}
    >
      <UpfrontPaymentFinancialModalWrapper
        deviceType={deviceType}
        allowScroll
        receipt={false}
      >
        <ProfileModalTitle deviceType={deviceType}>
          <ProfileModalTitleWrapper deviceType={deviceType}>
            <div>{t("upfrontPayment.stripe.payBill")}</div>
            <CloseIcon onClick={() => setStripeModalOpen(false)} />
          </ProfileModalTitleWrapper>
        </ProfileModalTitle>
        <div style={{ margin: paymentSubmitted ? "0px" : "20px" }}>
          {paymentSubmitted ? (
            <>
              <UpfrontPaymentReceipt
                modal
                setModal={setStripeModalOpen}
                isPaymentPlan={currentStep === StepperIndex.PaymentPlan}
                setActiveStep={setActiveStep}
                paymentAmount={paymentAmount}
              />
            </>
          ) : (
            <>
              {errorMessage && (
                <ErrorPopup
                  message={errorMessage}
                  hideMessage={() => setErrorMessage("")}
                />
              )}
              {!useNewCard && <SpinnerLoading />}
              <form
                style={{
                  display: useNewCard ? "grid" : "none",
                }}
                onSubmit={handleSubmit}
              >
                {!auth && (
                  <UpfrontPaymentStripeInputRow>
                    <div>
                      {" "}
                      {t("upfrontPayment.stripe.email")}{" "}
                      <UpfrontPaymentOptional>
                        {`${
                          currentStep === StepperIndex.PaymentPlan
                            ? ` ${t("upfrontPayment.stripe.required")}`
                            : ` ${t("upfrontPayment.stripe.optional")}`
                        }`}{" "}
                      </UpfrontPaymentOptional>
                    </div>
                    <UpfrontPaymentStripeInput
                      placeholder={t("upfrontPayment.stripe.email")}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                    {email && !validateEmail(email) && (
                      <UpfrontPaymentStripeInputError>
                        {" "}
                        {t("upfrontPayment.stripe.validEmail")}
                      </UpfrontPaymentStripeInputError>
                    )}
                  </UpfrontPaymentStripeInputRow>
                )}
                {!stripeReady && <SpinnerLoading />}
                <PaymentElement
                  options={{
                    paymentMethodOrder: ["card"],
                    layout: {
                      type: "tabs",
                    },
                  }}
                  onReady={() => setStripeReady(true)}
                />
                {auth && !useSavedPayment && stripeReady && (
                  <UpfrontPaymentSaveCreditCardGrid>
                    <input
                      type="checkbox"
                      onClick={() => {
                        setSaveCard(!saveCard);
                      }}
                      checked={saveCard}
                    />

                    <div>{t("upfrontPayment.stripe.saveCard")}</div>
                  </UpfrontPaymentSaveCreditCardGrid>
                )}
                {currentStep === StepperIndex.PaymentPlan && (
                  <UpfrontPaymentStripePaymentDisclosure>
                    {t("upfrontPayment.stripe.paymentPlanDisclosure")}
                  </UpfrontPaymentStripePaymentDisclosure>
                )}
                {loading && useNewCard ? (
                  <SpinnerLoading />
                ) : (
                  <>
                    {" "}
                    <UpfrontPaymentButtonRow>
                      <StyledButton
                        type="submit"
                        size="medium"
                        $backgroundColor="greenDark"
                        disabled={
                          !stripe ||
                          (currentStep === StepperIndex.PaymentPlan &&
                            !auth &&
                            !email) ||
                          (currentStep !== StepperIndex.PaymentPlan &&
                            !clientSecret) ||
                          Boolean(email && !validateEmail(email))
                        }
                      >
                        {currentStep === StepperIndex.PaymentPlan
                          ? t("upfrontPayment.paymentPlan.schedulePayments")
                          : t("upfrontPayment.actions.payAmount", {
                              amount: getCentsToDollars(paymentAmount),
                            })}
                      </StyledButton>
                    </UpfrontPaymentButtonRow>
                    <UpfrontPaymentButtonRow bottomRow tertiary>
                      <Button
                        label={t("upfrontPayment.actions.cancel")}
                        size="medium"
                        backgroundColor="greyMedium"
                        onClick={() => {
                          setErrorMessage("");
                          setStripeModalOpen(false);
                        }}
                        reverse
                      />
                    </UpfrontPaymentButtonRow>
                  </>
                )}
              </form>
            </>
          )}
        </div>
      </UpfrontPaymentFinancialModalWrapper>
    </Modal>
  );
};
